import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ensureAll } from '../../store/driversSlice';
import { default as FNS } from '../../api';
import { toast } from 'react-toastify';
import { formatDuration } from '../../util';
import { useReactToPrint } from 'react-to-print';
import { BrandBadge, DEFAULT_BRAND } from '../../brands';
import BrandFilter from '../../components/BrandFilter';

// Which company's jobs a report covers.
const REPORT_BRAND_GENITIVE = { abuss: 'ABussi', triobuss: 'TrioBussi' };

// Regular routes (arrangements) have no brand of their own; they are ABuss work.
const itemBrand = (item) => item.type === 'query' ? (item.query_brand || DEFAULT_BRAND) : DEFAULT_BRAND;

function DriverReports() {
    const dispatch = useDispatch();
    const [selectedDriverIds, setSelectedDriverIds] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [driversReportData, setDriversReportData] = useState({});
    const [loading, setLoading] = useState(false);
    const [driversEditableHours, setDriversEditableHours] = useState({});
    const [driversCustomRows, setDriversCustomRows] = useState({});
    const [generateForAllDrivers, setGenerateForAllDrivers] = useState(false);
    // Shared brand filter from the store; 'all' unless the user picked a brand
    const reportBrand = useSelector((state) => state.shownData.brandFilter) || 'all';
    const printRefs = useRef({});

    const drivers = useSelector((state) => state.drivers.knownDrivers);
    const haveAllDrivers = useSelector((state) => state.drivers.haveAll);

    useEffect(() => {
        dispatch(ensureAll());
    }, [dispatch]);

    // Set default dates (previous month)
    useEffect(() => {
        const now = new Date();

        // Previous month is always the full calendar month before `now`.
        // Handles new-year edge case (January -> December of previous year).
        // Date.UTC handles negative months (e.g. month -1 becomes December of previous year).
        const firstDayPrevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
        const lastDayPrevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));

        // Keep values in YYYY-MM-DD format for <input type="date" />
        setStartDate(firstDayPrevMonth.toISOString().split('T')[0]);
        setEndDate(lastDayPrevMonth.toISOString().split('T')[0]);
    }, []);

    // Clear selected drivers when unchecking "generate for all drivers"
    useEffect(() => {
        if (!generateForAllDrivers) {
            setSelectedDriverIds([]);
            setDriversReportData({});
            setDriversEditableHours({});
        }
    }, [generateForAllDrivers]);

    // A different brand is a different report: drop fetched data so it gets
    // regenerated, but keep the driver selection (the same driver can appear
    // in both the ABuss and the TrioBuss report).
    useEffect(() => {
        setDriversReportData({});
        setDriversEditableHours({});
        setDriversCustomRows({});
    }, [reportBrand]);

    const reportBrandGenitive = REPORT_BRAND_GENITIVE[reportBrand] || null;
    const reportTitle = reportBrandGenitive
        ? `${reportBrandGenitive} tööde juhi tundide aruanne`
        : 'Juhi tundide aruanne';
    // Same rule as the tables: only TrioBuss is badged, ABuss never is
    const showRowBrand = (item) => itemBrand(item) === 'triobuss';
    const showReportBrand = reportBrand === 'triobuss';

    // Helper functions for filtering job hours based on date range overlap
    const calculateJobDates = (startTimestamp, hoursArray) => {
        const jobDates = [];
        const startDate = new Date(startTimestamp * 1000);

        for (let i = 0; i < hoursArray.length; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            jobDates.push(currentDate.toISOString().split('T')[0]); // YYYY-MM-DD format
        }

        return jobDates;
    };

    const getDateRangeOverlap = (jobDates, rangeStart, rangeEnd) => {
        const overlapIndices = [];

        for (let i = 0; i < jobDates.length; i++) {
            const jobDate = jobDates[i];
            if (jobDate >= rangeStart && jobDate <= rangeEnd) {
                overlapIndices.push(i);
            }
        }

        return overlapIndices;
    };

    const filterJobForDateRange = (job, rangeStart, rangeEnd) => {
        if (job.type !== 'query' || !job.driver_feedback) {
            return job; // No filtering needed for arrangements or jobs without feedback
        }

        let feedback;
        try {
            feedback = JSON.parse(job.driver_feedback);
        } catch (e) {
            return job; // Invalid feedback, return as-is
        }

        if (!feedback.hours || !Array.isArray(feedback.hours)) {
            return job; // No hours array, return as-is
        }

        // Calculate job dates
        const jobDates = calculateJobDates(job.query_start, feedback.hours);

        // Get overlap indices
        const overlapIndices = getDateRangeOverlap(jobDates, rangeStart, rangeEnd);

        if (overlapIndices.length === 0) {
            // No overlap (shouldn't happen since backend filters, but safety check)
            return job;
        }

        if (overlapIndices.length === feedback.hours.length) {
            // Full overlap, no filtering needed
            return job;
        }

        // Partial overlap, filter the hours
        const filteredHours = overlapIndices.map(index => feedback.hours[index]);

        // Create modified feedback
        const modifiedFeedback = {
            ...feedback,
            hours: filteredHours
        };

        // Return job with modified feedback
        return {
            ...job,
            driver_feedback: JSON.stringify(modifiedFeedback)
        };
    };

    // Remove auto-fetching - only manual button fetch

    const fetchDriversReports = async () => {
        if (!startDate || !endDate) {
            toast.error('Palun valige kuupäevad');
            return;
        }

        const driverIds = generateForAllDrivers
            ? Object.values(drivers).filter(driver => !driver.driver_hidden).map(driver => driver.driver_id)
            : selectedDriverIds;

        if (!generateForAllDrivers && selectedDriverIds.length === 0) {
            toast.error('Palun valige vähemalt üks juht');
            return;
        }

        setLoading(true);
        try {
            const newReportData = {};
            const newEditableHours = {};

            // Fetch reports for selected drivers or all drivers
            await Promise.all(
                driverIds.map(async (driverId) => {
                    const result = await FNS.getDriverReport(driverId, startDate, endDate);

                    // Keep only the selected brand's jobs (so the TrioBuss report never
                    // contains ABuss hours and vice versa), then trim hours to the range
                    const filteredResult = result
                        .filter(item => reportBrand === 'all' || itemBrand(item) === reportBrand)
                        .map(item => filterJobForDateRange(item, startDate, endDate));

                    newReportData[driverId] = filteredResult;

                    // Initialize editable hours from the filtered report data (only for queries)
                    const initialHours = {};
                    filteredResult.forEach(item => {
                        if (item.type === 'query') {
                            if (item.driver_feedback) {
                                try {
                                    const feedback = JSON.parse(item.driver_feedback);
                                    // Handle hours as array or single value
                                    if (Array.isArray(feedback.hours)) {
                                        initialHours[item.query_id] = feedback.hours;
                                    } else if (feedback.hours) {
                                        initialHours[item.query_id] = [feedback.hours];
                                    } else {
                                        initialHours[item.query_id] = [''];
                                    }
                                } catch (e) {
                                    initialHours[item.query_id] = [''];
                                }
                            } else {
                                initialHours[item.query_id] = [''];
                            }
                        } else if (item.type === 'arrangement') {
                            // Initialize default hours for arrangements with duration value
                            const durationInHours = convertDurationToHours(item.arrangement_duration);
                            initialHours[item.arrangement_id + '_' + item.arrangement_date] = [durationInHours.toString()];
                        }
                    });
                    newEditableHours[driverId] = initialHours;

                    // Initialize custom rows for new drivers (preserve existing ones)
                    if (!driversCustomRows[driverId]) {
                        setDriversCustomRows(prev => ({
                            ...prev,
                            [driverId]: [{ id: Date.now() + Math.random(), title: '', hours: '' }]
                        }));
                    }
                })
            );

            setDriversReportData(newReportData);
            setDriversEditableHours(newEditableHours);

            // When generating for all drivers, update selectedDriverIds to match
            if (generateForAllDrivers) {
                setSelectedDriverIds(driverIds);
            }
        } catch (error) {
            toast.error('Viga aruande laadimisel: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await fetchDriversReports();
    };

    const calculateTotalHours = (driverId) => {
        const reportData = driversReportData[driverId];
        const editableHours = driversEditableHours[driverId];
        const customRows = driversCustomRows[driverId];

        if (!reportData) return 0;

        // Calculate total from all items (both queries and arrangements)
        const itemHoursTotal = reportData.reduce((total, item) => {
            let itemKey;
            if (item.type === 'query') {
                itemKey = item.query_id;
            } else if (item.type === 'arrangement') {
                itemKey = item.arrangement_id + '_' + item.arrangement_date;
            }

            const hoursArray = editableHours?.[itemKey] || [];
            const itemTotal = hoursArray.reduce((itemSum, hours) => {
                return itemSum + (parseFloat(hours) || 0);
            }, 0);
            return total + itemTotal;
        }, 0);

        // Calculate total from custom rows
        const customHoursTotal = (customRows || []).reduce((total, row) => {
            return total + (parseFloat(row.hours) || 0);
        }, 0);

        return itemHoursTotal + customHoursTotal;
    };

    const handleHoursChange = (driverId, queryId, index, newHours) => {
        setDriversEditableHours(prev => ({
            ...prev,
            [driverId]: {
                ...prev[driverId],
                [queryId]: [
                    ...(prev[driverId]?.[queryId] || ['']).slice(0, index),
                    newHours,
                    ...(prev[driverId]?.[queryId] || ['']).slice(index + 1)
                ]
            }
        }));
    };

    const addCustomRow = (driverId) => {
        setDriversCustomRows(prev => ({
            ...prev,
            [driverId]: [
                ...(prev[driverId] || []),
                { id: Date.now() + Math.random(), title: '', hours: '' }
            ]
        }));
    };

    const updateCustomRow = (driverId, id, field, value) => {
        setDriversCustomRows(prev => ({
            ...prev,
            [driverId]: (prev[driverId] || []).map(row =>
                row.id === id ? { ...row, [field]: value } : row
            )
        }));
    };

    const removeCustomRow = (driverId, id) => {
        setDriversCustomRows(prev => {
            const driverRows = prev[driverId] || [];
            if (driverRows.length > 1) {
                return {
                    ...prev,
                    [driverId]: driverRows.filter(row => row.id !== id)
                };
            }
            return prev;
        });
    };

    const addDriver = (driverId) => {
        if (!selectedDriverIds.includes(driverId)) {
            setSelectedDriverIds(prev => [...prev, driverId]);
            // Initialize custom rows for the new driver
            setDriversCustomRows(prev => ({
                ...prev,
                [driverId]: [{ id: Date.now() + Math.random(), title: '', hours: '' }]
            }));
        }
    };

    const removeDriver = (driverId) => {
        setSelectedDriverIds(prev => prev.filter(id => id !== driverId));
        // Clean up data for removed driver
        setDriversReportData(prev => {
            const { [driverId]: removed, ...rest } = prev;
            return rest;
        });
        setDriversEditableHours(prev => {
            const { [driverId]: removed, ...rest } = prev;
            return rest;
        });
        // Keep custom rows data in case driver is re-added
    };

    const formatDateTime = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleString('et-EE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('et-EE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatArrangementDate = (dateString) => {
        return new Date(dateString + 'T00:00:00').toLocaleDateString('et-EE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatArrangementDuration = (durationString) => {
        // Convert "03:15" to "3 h 15 min" format
        const [hours, minutes] = durationString.split(':').map(Number);
        let result = '';

        if (hours > 0) {
            result += `${hours} h`;
        }
        if (minutes > 0) {
            if (result) result += ' ';
            result += `${minutes} min`;
        }
        if (!result) result = '0 min';

        return result;
    };

    const convertDurationToHours = (durationString) => {
        // Convert "03:15" to decimal hours (3.25)
        const [hours, minutes] = durationString.split(':').map(Number);
        return hours + (minutes / 60);
    };

    const formatArrangementTime = (dateString, departTime) => {
        return `${formatArrangementDate(dateString)} ${departTime}`;
    };

    // Render a query row
    // dayColorHistory is an array of objects: { date: string, isAlt: boolean }
    const renderQueryRow = (query, index, driverId, editableHours, dayColorHistory) => {
        let feedback = null;
        if(query.driver_feedback) {
            try {
                feedback = JSON.parse(query.driver_feedback);
            } catch(e) {
                // Invalid JSON, ignore
            }
        }

        const currentDate = formatDate(query.query_start);
        let useAltColor = false;

        if(dayColorHistory.length === 0) {
            // First day: start with base color (white => isAlt = false)
            useAltColor = false;
        } else {
            const lastEntry = dayColorHistory[dayColorHistory.length - 1];
            if(lastEntry.date === currentDate) {
                // Same day as previous row, keep same color
                useAltColor = lastEntry.isAlt;
            } else {
                // New day: flip color
                useAltColor = !lastEntry.isAlt;
            }
        }

        // Record this row's day/color for the next calls
        dayColorHistory.push({ date: currentDate, isAlt: useAltColor });

        return (
            <div key={query.query_id} className="row g8 p8" style={{
                borderBottom: '1px solid #eee',
                backgroundColor: useAltColor ? '#f9f9f9' : 'white'
            }}>
                <div style={{ flex: '0 0 100px', fontSize: '0.9em', paddingRight: '8px' }}>
                    {formatDate(query.query_start)}
                </div>

                <div style={{ flex: '1', fontSize: '0.9em', paddingRight: '8px', minWidth: 0 }}>
                    <a
                        href={`/portal/platform/job/${query.query_id}`}
                        style={{
                            display: 'block',
                            fontWeight: 'bold',
                            color: 'black',
                            textDecoration: 'none',
                            transition: 'all 0.2s',
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            maxWidth: 'min-content',
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.color = 'blue';
                            e.target.style.cursor = 'pointer';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.color = 'black';
                        }}
                    >
                        {JSON.parse(query["query_route"]).map(route => route["city_name"]).join(" - ")}
                    </a>
                    {showRowBrand(query) && (
                        <div style={{ marginTop: 2 }}><BrandBadge brand={itemBrand(query)} /></div>
                    )}
                </div>

                <div style={{ flex: '0 0 80px', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingRight: '8px' }}>
                    <div className="column g4">
                        {(editableHours?.[query.query_id] || ['']).map((hours, hourIndex) => (
                            <input
                                key={hourIndex}
                                type="number"
                                step="0.1"
                                value={hours}
                                onChange={(e) => handleHoursChange(driverId, query.query_id, hourIndex, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                style={{
                                    width: '60px',
                                    padding: '2px 4px',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    fontSize: '0.9em'
                                }}
                                placeholder="0"
                            />
                        ))}
                    </div>
                </div>

                <div style={{ flex: '0 0 160px', display: 'flex', alignItems: 'center' }}>
                    {feedback ? (
                        <span style={{ color: feedback.wentWell ? 'green' : 'red' }}>
                            {feedback.comment || 'Tagasiside olemas'}
                        </span>
                    ) : (
                        <span style={{ color: 'red' }}>
                            Tagasiside puudub
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Render an arrangement row
    const renderArrangementRow = (arrangement, index, driverId, editableHours, dayColorHistory) => {
        const arrangementKey = arrangement.arrangement_id + '_' + arrangement.arrangement_date;

        const currentDate = formatArrangementDate(arrangement.arrangement_date);
        let useAltColor = false;

        if(dayColorHistory.length === 0) {
            // First day: start with base color (white => isAlt = false)
            useAltColor = false;
        } else {
            const lastEntry = dayColorHistory[dayColorHistory.length - 1];
            if(lastEntry.date === currentDate) {
                // Same day as previous row, keep same color
                useAltColor = lastEntry.isAlt;
            } else {
                // New day: flip color
                useAltColor = !lastEntry.isAlt;
            }
        }

        // Record this row's day/color for the next calls
        dayColorHistory.push({ date: currentDate, isAlt: useAltColor });

        return (
            <div key={arrangementKey} className="row g8 p8" style={{
                borderBottom: '1px solid #eee',
                backgroundColor: useAltColor ? '#f9f9f9' : 'white'
            }}>
                <div style={{ flex: '0 0 100px', fontSize: '0.9em', paddingRight: '8px' }}>
                    {formatArrangementDate(arrangement.arrangement_date)}
                </div>

                <div style={{ flex: '1', fontSize: '0.9em', paddingRight: '8px', minWidth: 0 }}>
                    <a
                        href={`/portal/platform/arrangement/${arrangement.arrangement_id}`}
                        style={{
                            fontWeight: 'bold',
                            maxWidth: 'min-content',
                            color: 'black',
                            textDecoration: 'none',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.color = 'blue';
                            e.target.style.cursor = 'pointer';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.color = 'black';
                        }}
                    >
                        {arrangement.arrangement_name}
                    </a>
                    {arrangement.arrangement_description && (
                        <div style={{ opacity: 0.6, fontSize: '0.8em' }}>
                            {arrangement.arrangement_description}
                        </div>
                    )}
                    <div style={{ opacity: 0.5, fontSize: '0.7em', fontStyle: 'italic' }}>
                        Regulaarvedu
                    </div>
                </div>

                <div style={{ flex: '0 0 80px', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingRight: '8px' }}>
                    <div className="column g4">
                        {(editableHours?.[arrangementKey] || ['']).map((hours, hourIndex) => (
                            <input
                                key={hourIndex}
                                type="number"
                                step="0.1"
                                value={hours}
                                onChange={(e) => handleHoursChange(driverId, arrangementKey, hourIndex, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                style={{
                                    width: '60px',
                                    padding: '2px 4px',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    fontSize: '0.9em'
                                }}
                                placeholder="0"
                            />
                        ))}
                    </div>
                </div>

                <div style={{ flex: '0 0 160px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>
                        Regulaarvedu
                    </span>
                </div>
            </div>
        );
    };

    // Create printable component for a single driver
    const PrintableDriverReport = ({ driverId, driver, reportData, editableHours, customRows, startDate, endDate }) => {
        return (
            <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <h2>{reportTitle}</h2>
                    <h3>{driver.driver_firstname} ({driver.driver_username})</h3>
                    <p>Periood: {startDate} - {endDate}</p>
                </div>

                <div style={{ border: '1px solid #333', fontSize: '14px' }}>
                    {/* Header row */}
                    <div style={{
                        display: 'flex',
                        fontWeight: 'bold',
                        borderBottom: '2px solid #333',
                        backgroundColor: '#f5f5f5',
                        padding: '8px'
                    }}>
                        <div style={{ flex: '0 0 100px', paddingRight: '8px' }}>Kuupäev</div>
                        <div style={{ flex: '0 0 320px', paddingRight: '8px' }}>Marsruut</div>
                        <div style={{ flex: '0 0 80px', paddingRight: '8px' }}>Tunnid</div>
                        <div style={{ flex: '1' }}>Tagasiside</div>
                    </div>

                    {/* Data rows */}
                    {(() => {
                        const dayColorHistory = [];
                        return reportData.map((item) => {
                            let itemKey, duration, status, route, date;

                            let routeLink;
                            const feedback = item.driver_feedback ? JSON.parse(item.driver_feedback) : null;
                            if(item.type === 'query') {
                                itemKey = item.query_id;
                                duration = formatDuration(item.query_duration);
                                status = feedback
                                    ? feedback.comment
                                    : 'Tagasiside puudub';
                                route = JSON.parse(item["query_route"]).map(route => route["city_name"]).join(" - ");
                                routeLink = `/portal/platform/job/${item.query_id}`;
                                date = formatDate(item.query_start);
                            } else if(item.type === 'arrangement') {
                                itemKey = item.arrangement_id + '_' + item.arrangement_date;
                                duration = formatArrangementDuration(item.arrangement_duration);
                                status = 'Regulaarvedu';
                                route = item.arrangement_name;
                                routeLink = `/portal/platform/arrangement/${item.arrangement_id}`;
                                date = formatArrangementDate(item.arrangement_date);
                            }

                            const hoursArray = editableHours?.[itemKey] || [''];

                            // Determine background color by day using same logic as screen rows
                            const currentDate = item.type === 'query'
                                ? formatDate(item.query_start)
                                : formatArrangementDate(item.arrangement_date);
                            let useAltColor = false;

                            if(dayColorHistory.length === 0) {
                                // First day: start with base color (white => isAlt = false)
                                useAltColor = false;
                            } else {
                                const lastEntry = dayColorHistory[dayColorHistory.length - 1];
                                if(lastEntry.date === currentDate) {
                                    // Same day as previous row, keep same color
                                    useAltColor = lastEntry.isAlt;
                                } else {
                                    // New day: flip color
                                    useAltColor = !lastEntry.isAlt;
                                }
                            }

                            // Record this row's day/color for the next calls
                            dayColorHistory.push({ date: currentDate, isAlt: useAltColor });

                            return (
                                <div key={itemKey} style={{
                                    display: 'flex',
                                    padding: '8px',
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: useAltColor ? '#f9f9f9' : 'white'
                                }}>
                                    <div style={{ flex: '0 0 100px', fontSize: '0.9em', paddingRight: '8px' }}>
                                        {date}
                                    </div>
                                    <div style={{ flex: '0 0 320px', fontSize: '0.9em', paddingRight: '8px', minWidth: 0 }}>
                                        <a
                                            href={routeLink}
                                            style={{
                                                display: 'block',
                                                minWidth: 0,
                                                maxWidth: 'min-content',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textDecoration: 'none',
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden"
                                            }}
                                        >
                                            {route}
                                        </a>
                                        {showRowBrand(item) && (
                                            <div style={{ marginTop: 2 }}><BrandBadge brand={itemBrand(item)} /></div>
                                        )}
                                        {item.type === 'arrangement' && (
                                            <div style={{ opacity: 0.5, fontSize: '0.7em', fontStyle: 'italic' }}>
                                                Regulaarvedu
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', paddingRight: '8px' }}>
                                        {hoursArray.map((hours, hourIndex) => (
                                            <div key={hourIndex} style={{ fontSize: '0.9em' }}>
                                                {parseFloat(hours || 0).toFixed(2)} h
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        {status}
                                    </div>
                                </div>
                            );
                        });
                    })()}

                    {/* Custom rows */}
                    {customRows && customRows.filter(row =>
                        parseFloat(row.hours || 0) > 0 || (row.title && row.title.trim().length > 0)
                    ).map((row) => (
                        <div key={row.id} style={{
                            display: 'flex',
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            backgroundColor: '#f0f8ff',
                            fontStyle: 'italic'
                        }}>
                            <div style={{ flex: '0 0 100px', paddingRight: '8px' }}>-</div>
                            <div style={{ flex: '0 0 320px', paddingRight: '8px' }}>{row.title}</div>
                            <div style={{ flex: '0 0 80px', paddingRight: '8px' }}>-</div>
                            <div style={{ flex: '1', textAlign: 'center', paddingRight: '8px' }}>
                                {parseFloat(row.hours || 0).toFixed(2)} h
                            </div>
                            <div style={{ flex: '0 0 150px' }}>Lisatöö</div>
                        </div>
                    ))}

                    {/* Total row */}
                    <div style={{
                        display: 'flex',
                        padding: '8px',
                        borderTop: '2px solid #333',
                        backgroundColor: '#e8f4f8',
                        fontWeight: 'bold'
                    }}>
                        <div style={{ flex: '0 0 100px', paddingRight: '8px' }}></div>
                        <div style={{ flex: '0 0 320px', paddingRight: '8px' }}>KOKKU:</div>
                        <div style={{ flex: '0 0 80px', paddingRight: '8px' }}></div>
                        <div style={{ flex: '1', textAlign: 'center', paddingRight: '8px' }}>
                            {calculateTotalHours(driverId).toFixed(2)} h
                        </div>
                        <div style={{ flex: '0 0 150px' }}></div>
                    </div>
                </div>
            </div>
        );
    };

    // Calculate summary statistics
    const calculateSummaryStats = () => {
        let totalHours = 0;
        let activeDriverCount = 0;

        selectedDriverIds.forEach(driverId => {
            const reportData = driversReportData[driverId];
            if (reportData && reportData.length > 0) {
                activeDriverCount++;
                totalHours += calculateTotalHours(driverId);
            }
        });

        return { totalHours, activeDriverCount };
    };

    const driversArray = Object.values(drivers);

    // Create print handlers for each selected driver
    const createPrintHandler = (driverId, driver) => {
        return () => {
            if (printRefs.current[driverId]) {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    const printContent = printRefs.current[driverId].innerHTML;
                    printWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                            <title>Juhi_aruanne_${reportBrandGenitive ? reportBrandGenitive + '_' : ''}${driver.driver_firstname}_${startDate}_${endDate}</title>
                            <style>
                                @page {
                                    margin: 20px;
                                }
                                @media print {
                                    body { -webkit-print-color-adjust: exact; }
                                }
                                body {
                                    margin: 0;
                                    font-family: Arial, sans-serif;
                                }
                            </style>
                        </head>
                        <body>
                            ${printContent}
                        </body>
                        </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                }
            }
        };
    };

    return (
        <div className="column g32 p32">
            <div className="column g16">
                <h2>Juhi tundide aruanne</h2>

                <form onSubmit={handleSubmit} className="column g16">
                    <div className="column g16 p16" style={{ border: '1px solid #ddd' }}>
                        <div className="line g16">
                            <div className="column g8">
                                <label htmlFor="start-date">Alguskuupäev:</label>
                                <input
                                    type="date"
                                    id="start-date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    disabled={loading}
                                />
                            </div>

                            <div className="column g8">
                                <label htmlFor="end-date">Lõppkuupäev:</label>
                                <input
                                    type="date"
                                    id="end-date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    disabled={loading}
                                />
                            </div>

                            <div className="column g8">
                                <label>Tööd:</label>
                                <BrandFilter />
                            </div>
                        </div>

                        <div className="column g8">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={generateForAllDrivers}
                                    onChange={(e) => setGenerateForAllDrivers(e.target.checked)}
                                    disabled={loading}
                                    style={{ marginRight: '8px' }}
                                />
                                Koosta kõikide juhtide jaoks
                            </label>
                        </div>

                        {!generateForAllDrivers && (
                            <div className="column g8">
                                <label>Juhid:</label>
                            <div className="line g8">
                                <select
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            addDriver(e.target.value);
                                            e.target.value = '';
                                        }
                                    }}
                                    disabled={!haveAllDrivers || loading}
                                >
                                    <option value="">Lisa juht...</option>
                                    {driversArray
                                        .filter(driver => !driver.driver_hidden && !selectedDriverIds.includes(driver.driver_id))
                                        .sort((a, b) => a.driver_firstname.localeCompare(b.driver_firstname))
                                        .map(driver => (
                                            <option key={driver.driver_id} value={driver.driver_id}>
                                                {driver.driver_firstname} ({driver.driver_username})
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>

                            {selectedDriverIds.length > 0 && (
                                <div className="column g4">
                                    <small>Valitud juhid:</small>
                                    <div className="line g8">
                                        {selectedDriverIds
                                                .sort((a, b) => {
                                                    const driverA = driversArray.find(d => d.driver_id === a);
                                                    const driverB = driversArray.find(d => d.driver_id === b);
                                                    if (driverA && driverB) {
                                                        return driverA.driver_firstname.localeCompare(driverB.driver_firstname);
                                                    }
                                                    return 0;
                                                })
                                                .map(driverId => {
                                            const driver = driversArray.find(d => d.driver_id === driverId);
                                            return driver ? (
                                                <div key={driverId} className="line g4 center-cross" style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: '#e8f4f8',
                                                    borderRadius: '4px',
                                                    fontSize: '0.9em'
                                                }}>
                                                    <span>{driver.driver_firstname}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDriver(driverId)}
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            padding: '0',
                                                            border: 'none',
                                                            borderRadius: '2px',
                                                            backgroundColor: '#ff4444',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}
                            </div>
                        )}

                        <button type="submit" disabled={loading || (!generateForAllDrivers && selectedDriverIds.length === 0)}>
                            {loading ? 'Laadib...' : 'Uuenda aruandeid'}
                        </button>
                    </div>
                </form>
            </div>

            {selectedDriverIds.length > 0 && Object.keys(driversReportData).length > 0 && (
                <div className="column g32">
                    {/* Summary section - only for screen, not PDF */}
                    {(() => {
                        const { totalHours, activeDriverCount } = calculateSummaryStats();
                        return (
                            <div className="column g16" style={{
                                padding: '20px',
                                backgroundColor: '#e8f4f8',
                                borderRadius: '8px',
                                border: '1px solid #cce7f0'
                            }}>
                                <h3 style={{ margin: '0 0 16px 0', color: '#2c5aa0' }}>
                                    Kokkuvõte{showReportBrand && <> <BrandBadge brand={reportBrand} /></>}
                                </h3>
                                <div className="row g32">
                                    <div className="column g8">
                                        <div style={{ fontSize: '0.9em', color: '#666' }}>Kokku tunde:</div>
                                        <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#2c5aa0' }}>
                                            {totalHours.toFixed(2)} h
                                        </div>
                                    </div>
                                    <div className="column g8">
                                        <div style={{ fontSize: '0.9em', color: '#666' }}>Aktiivseid juhte:</div>
                                        <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#2c5aa0' }}>
                                            {activeDriverCount} aktiivset juhti
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {selectedDriverIds
                            .sort((a, b) => {
                                const driverA = driversArray.find(d => d.driver_id === a);
                                const driverB = driversArray.find(d => d.driver_id === b);
                                const reportDataA = driversReportData[a];
                                const reportDataB = driversReportData[b];

                                const hasDataA = reportDataA && reportDataA.length > 0;
                                const hasDataB = reportDataB && reportDataB.length > 0;

                                if(hasDataA !== hasDataB) {
                                    return hasDataA ? -1 : 1;
                                }

                                if(driverA && driverB) {
                                    return driverA.driver_firstname.localeCompare(driverB.driver_firstname);
                                }
                                return 0;
                            })
                            .map(driverId => {
                        const driver = driversArray.find(d => d.driver_id === driverId);
                        const reportData = driversReportData[driverId];
                        const editableHours = driversEditableHours[driverId];
                        const customRows = driversCustomRows[driverId] || [];

                        if (!driver || !reportData) return null;

                        return (
                            <div key={driverId} className="column" style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                backgroundColor: '#fafafa'
                            }}>
                                {/* Hidden printable component */}
                                <div style={{ display: 'none' }}>
                                    <div ref={(el) => (printRefs.current[driverId] = el)}>
                                        <PrintableDriverReport
                                            driverId={driverId}
                                            driver={driver}
                                            reportData={reportData}
                                            editableHours={editableHours}
                                            customRows={customRows}
                                            startDate={startDate}
                                            endDate={endDate}
                                        />
                                    </div>
                                </div>

                                <div className="row g16 p8 center-cross">
                                    <h3>{driver.driver_firstname} ({driver.driver_username})</h3>
                                    {showReportBrand && <BrandBadge brand={reportBrand} />}
                                    <div className="grow row g8 center-cross">
                                        <span>Kokku tunde:</span>
                                        <strong>{calculateTotalHours(driverId).toFixed(2)} h</strong>
                                    </div>
                                    <button
                                        onClick={createPrintHandler(driverId, driver)}
                                        style={{
                                            padding: '6px 12px',
                                            border: '1px solid #007acc',
                                            borderRadius: '4px',
                                            backgroundColor: '#007acc',
                                            color: 'white',
                                            fontSize: '0.9em',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Salvesta PDF
                                    </button>
                                </div>

                                {reportData.length === 0 ? (
                                    <p class="p8">Valitud perioodil töid ei leitud</p>
                                ) : (
                                    <div className="column">
                                        <div className="row g8 p8" style={{
                                            fontWeight: 'bold',
                                            borderTop: '1px solid #ddd',
                                            backgroundColor: '#f5f5f5'
                                        }}>
                                            <div style={{ flex: '0 0 100px' }}>Kuupäev</div>
                                            <div style={{ flex: '1' }}>Marsruut</div>
                                            <div style={{ flex: '0 0 80px' }}>Tunnid</div>
                                            <div style={{ flex: '0 0 150px' }}>Tagasiside</div>
                                        </div>

                                        {(() => {
                                            const dayColorHistory = [];
                                            return reportData.map((item, index) => {
                                                if(item.type === 'query') {
                                                    return renderQueryRow(item, index, driverId, editableHours, dayColorHistory);
                                                } else if(item.type === 'arrangement') {
                                                    return renderArrangementRow(item, index, driverId, editableHours, dayColorHistory);
                                                }
                                                return null;
                                            });
                                        })()}

                                        {/* Custom time rows */}
                                        {customRows.map((row) => (
                                            <div key={row.id} className="row g8 p8" style={{
                                                borderBottom: '1px solid #eee',
                                                backgroundColor: '#f0f8ff',
                                                fontStyle: 'italic'
                                            }}>
                                                <div style={{ flex: '0 0 100px', fontSize: '0.9em' }}>
                                                    -
                                                </div>

                                                <div className="row center-cross g8" style={{ flex: '1', fontSize: '0.9em' }}>
                                                    <input
                                                        type="text"
                                                        value={row.title}
                                                        onChange={(e) => updateCustomRow(driverId, row.id, 'title', e.target.value)}
                                                        placeholder="Lisa ajakulu lisamine ..."
                                                        style={{
                                                            width: '100%',
                                                            padding: '4px 6px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '3px',
                                                            fontSize: '0.9em',
                                                            backgroundColor: 'white'
                                                        }}
                                                    />
                                                    {customRows.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeCustomRow(driverId, row.id)}
                                                            style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                padding: '0',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                backgroundColor: '#ff4444',
                                                                color: 'white',
                                                                fontSize: '12px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                            title="Eemalda rida"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>

                                                <div style={{ flex: '0 0 80px' }}>

                                                </div>

                                                <div style={{ flex: '0 0 80px' }}>
                                                    <div className="row g4 center-cross">
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            value={row.hours}
                                                            onChange={(e) => updateCustomRow(driverId, row.id, 'hours', e.target.value)}
                                                            onFocus={(e) => e.target.select()}
                                                            style={{
                                                                width: '60px',
                                                                padding: '2px 12px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '3px',
                                                                fontSize: '0.9em'
                                                            }}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                </div>

                                                <div style={{ flex: '0 0 150px' }}>
                                                    <span style={{ color: '#666' }}>
                                                        Lisatöö
                                                    </span>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Add new custom row button */}
                                        <div className="row g8 p8" style={{
                                            borderBottom: '1px solid #eee',
                                        }}>
                                            <div style={{ flex: '0 0 100px' }}></div>
                                            <div style={{ flex: '1' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => addCustomRow(driverId)}
                                                    style={{
                                                        padding: '4px 12px',
                                                        border: '1px solid #007acc',
                                                        borderRadius: '3px',
                                                        backgroundColor: '#007acc',
                                                        color: 'white',
                                                        fontSize: '0.9em',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    + Lisa ajakulu rida
                                                </button>
                                            </div>
                                            <div style={{ flex: '0 0 80px' }}></div>
                                            <div style={{ flex: '0 0 80px' }}></div>
                                            <div style={{ flex: '0 0 150px' }}></div>
                                        </div>

                                        {/* Total row */}
                                        <div className="row g8 p8" style={{
                                            fontWeight: 'bold',
                                            borderTop: '2px solid #333',
                                            backgroundColor: '#e8f4f8'
                                        }}>
                                            <div style={{ flex: '0 0 100px' }}></div>
                                            <div style={{ flex: '1' }}>KOKKU:</div>
                                            <div style={{ flex: '0 0 80px' }}></div>
                                            <div style={{ flex: '0 0 80px', fontWeight: 'bold' }}>
                                                {calculateTotalHours(driverId).toFixed(2)} h
                                            </div>
                                            <div style={{ flex: '0 0 150px' }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default DriverReports;